import React, { useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import SEO, { getServiceSchema, getBreadcrumbSchema } from '../../components/SEO';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * CREATIVE STUDIO & DIGITAL PRICING PAGE - /pricing/creative-digital
 * ═══════════════════════════════════════════════════════════════════════════
 */

const PricingCreativeDigital = () => {
  const location = useLocation();

  // Handle hash scroll on page load
  useEffect(() => {
    if (location.hash) {
      const scrollToHash = () => {
        const element = document.querySelector(location.hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      };
      
      if (document.readyState === 'complete') {
        setTimeout(scrollToHash, 100);
      } else {
        window.addEventListener('load', () => setTimeout(scrollToHash, 100), { once: true });
      }
      
      const backupTimer = setTimeout(scrollToHash, 800);
      return () => clearTimeout(backupTimer);
    }
  }, [location.hash]);

  return (
    <div className="flex flex-col min-h-screen">
      {/* SEO Meta Tags */}
      <SEO
        title="Creative Studio & AI Services Pricing | RC Studios"
        description="AI-powered digital persona development, photo restoration, LoRA training, creative retouching, and custom artwork. Innovative digital media services nationwide."
        path="/pricing/creative-digital"
        jsonLd={[
          getServiceSchema({
            name: 'Creative Studio & Digital Services',
            description: 'AI-powered digital persona development, photo restoration, LoRA training, creative retouching, and custom artwork services.',
            url: 'https://rcstudios.net/pricing/creative-digital',
            areaServed: 'United States',
            priceRange: '$25-$2,500+'
          }),
          getBreadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Pricing', path: '/pricing' },
            { name: 'Creative & Digital', path: '/pricing/creative-digital' }
          ])
        ]}
      />

      {/* Hero Section */}
      <section className="bg-[#050816] text-white pt-32 pb-20">
        <div className="container mx-auto px-6 max-w-[1000px]">
          {/* Back Link */}
          <Link 
            to="/pricing" 
            className="inline-flex items-center gap-2 text-gray-400 hover:text-white text-sm font-medium mb-8 transition-colors"
            data-testid="back-to-categories"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Categories
          </Link>
          
          <div className="text-center">
            <p className="text-[#F5C056] text-xs font-bold uppercase tracking-[0.2em] mb-4">Creative Studio & Digital</p>
            <h1 className="font-serif font-semibold text-4xl md:text-5xl mb-6">
              Creative & Digital Services
            </h1>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              Digital innovation meets artistic excellence. AI-powered solutions and creative services.
            </p>
          </div>
        </div>
      </section>

      {/* Digital Persona Development Section */}
      <section id="digital-persona" className="py-20 bg-[#FAF9F6] scroll-mt-24">
        <div className="container mx-auto px-6 max-w-[1200px]">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Image */}
            <div className="relative rounded-2xl overflow-hidden shadow-xl aspect-[4/5]">
              <img
                src="https://images.unsplash.com/photo-1620712943543-bcc4688e7485?auto=format&fit=crop&q=80&w=800"
                alt="Digital Persona Development"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
              <div className="absolute bottom-0 left-0 p-8 text-white">
                <p className="text-[#F5C056] text-xs font-bold uppercase tracking-widest mb-2">Flagship Service</p>
                <h3 className="font-serif text-2xl font-bold">Digital Persona Development</h3>
              </div>
            </div>

            {/* Content */}
            <div className="space-y-6">
              <h2 className="font-serif text-3xl md:text-4xl text-gray-900">Digital Persona Development</h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                Build a consistent on-camera identity with cinematic visuals, repeatable content, and brand-ready assets. 
                Our flagship AI-powered service creates your unique digital presence.
              </p>
              
              <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
                <div className="flex items-baseline gap-2 mb-4">
                  <p className="text-sm text-muted-foreground">Starting at</p>
                  <p className="text-3xl font-serif font-medium text-gray-900">$500</p>
                </div>
                
                <div className="space-y-3 mb-6">
                  {[
                    "Persona identity + visual direction",
                    "Cinematic image set (brand-ready)",
                    "Prompt framework / style blueprint",
                    "Content pillars + posting angles",
                    "Optional: custom AI model / LoRA workflow"
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#F5C056] mt-2"></div>
                      <span className="text-gray-600 text-sm">{item}</span>
                    </div>
                  ))}
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <Button asChild className="flex-1 bg-[#F5C056] text-[#050816] hover:bg-[#F5C056]/90 rounded-lg h-11 font-semibold">
                    <Link to="/services/digital-persona-development">Learn More</Link>
                  </Button>
                  <Button asChild variant="outline" className="flex-1 border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg h-11">
                    <Link to="/contact">Request Consultation</Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AI Upscaling & Restoration Section */}
      <section id="restoration" className="py-20 bg-white scroll-mt-24">
        <div className="container mx-auto px-6 max-w-[1200px]">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6 order-2 lg:order-1">
              <h2 className="font-serif text-3xl md:text-4xl text-gray-900">AI Upscaling & Restoration</h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                Breathe new life into old, damaged, or low-resolution photos using advanced AI restoration technology.
              </p>
              
              <div className="bg-[#FAF9F6] rounded-xl p-6 border border-gray-200 shadow-sm">
                <div className="flex items-baseline gap-2 mb-4">
                  <p className="text-sm text-muted-foreground">Starting at</p>
                  <p className="text-3xl font-serif font-medium text-gray-900">$25</p>
                  <p className="text-sm text-muted-foreground">/ image</p>
                </div>
                
                <div className="space-y-3 mb-6">
                  {[
                    "AI-powered upscaling (up to 8x)",
                    "Scratch & damage repair",
                    "Color restoration & enhancement",
                    "Face enhancement technology",
                    "Colorization of B&W photos"
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#F5C056] mt-2"></div>
                      <span className="text-gray-600 text-sm">{item}</span>
                    </div>
                  ))}
                </div>

                <Button asChild className="w-full bg-[#050816] text-white hover:bg-[#050816]/90 rounded-lg h-11">
                  <Link to="/services/photo-restoration">Order Restoration</Link>
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 order-1 lg:order-2">
              <div className="relative rounded-xl overflow-hidden shadow-lg">
                <img
                  src="https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&q=80&w=400&sat=-100"
                  alt="Before restoration"
                  className="w-full aspect-[3/4] object-cover grayscale"
                />
                <div className="absolute top-3 left-3 bg-white/90 px-2 py-1 rounded text-xs font-semibold">Before</div>
              </div>
              <div className="relative rounded-xl overflow-hidden shadow-lg">
                <img
                  src="https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&q=80&w=400"
                  alt="After restoration"
                  className="w-full aspect-[3/4] object-cover"
                />
                <div className="absolute top-3 left-3 bg-[#F5C056] px-2 py-1 rounded text-xs font-semibold">After</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Creative Retouching Section */}
      <section id="retouching" className="py-20 bg-[#FAF9F6] scroll-mt-24">
        <div className="container mx-auto px-6 max-w-[1200px]">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl md:text-4xl text-gray-900 mb-4">Creative Retouching</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Professional post-production and retouching for flawless, polished imagery.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-[900px] mx-auto">
            {[
              { title: 'Basic Retouch', price: '$15', per: '/image', features: ['Exposure correction', 'Color grading', 'Minor blemish removal'] },
              { title: 'Advanced Retouch', price: '$45', per: '/image', features: ['Full skin retouching', 'Background cleanup', 'Body contouring', 'Color matching'] },
              { title: 'Editorial', price: '$100', per: '/image', features: ['High-end beauty retouch', 'Compositing', 'Creative effects', 'Multiple revisions'] },
            ].map((pkg, i) => (
              <div key={i} className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-lg transition-all">
                <h3 className="font-serif text-xl text-gray-900 mb-2">{pkg.title}</h3>
                <div className="flex items-baseline gap-1 mb-4">
                  <p className="text-2xl font-serif font-medium text-gray-900">{pkg.price}</p>
                  <p className="text-sm text-gray-500">{pkg.per}</p>
                </div>
                <ul className="space-y-2 mb-6">
                  {pkg.features.map((f, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-gray-600">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#F5C056] mt-1.5"></div>
                      {f}
                    </li>
                  ))}
                </ul>
                <Button asChild variant="outline" className="w-full border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg">
                  <Link to="/contact">Get Quote</Link>
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Custom Artwork & Prints Section */}
      <section id="artwork" className="py-20 bg-white scroll-mt-24">
        <div className="container mx-auto px-6 max-w-[1200px]">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative rounded-2xl overflow-hidden shadow-xl aspect-square">
              <img
                src="https://images.unsplash.com/photo-1579783902614-a3fb3927b6a5?auto=format&fit=crop&q=80&w=800"
                alt="Custom Artwork"
                className="w-full h-full object-cover"
              />
            </div>

            <div className="space-y-6">
              <h2 className="font-serif text-3xl md:text-4xl text-gray-900">Custom Artwork & Prints</h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                One-of-a-kind visual pieces and fine art prints for homes, offices, and collections. 
                From digital art to gallery-quality prints.
              </p>
              
              <div className="bg-[#FAF9F6] rounded-xl p-6 border border-gray-200 shadow-sm">
                <div className="flex items-baseline gap-2 mb-4">
                  <p className="text-sm text-muted-foreground">Custom pieces starting at</p>
                  <p className="text-3xl font-serif font-medium text-gray-900">$200</p>
                </div>
                
                <div className="space-y-3 mb-6">
                  {[
                    "Custom digital artwork",
                    "Fine art print preparation",
                    "Gallery-quality printing",
                    "Framing consultation",
                    "Limited edition runs"
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#F5C056] mt-2"></div>
                      <span className="text-gray-600 text-sm">{item}</span>
                    </div>
                  ))}
                </div>

                <Button asChild className="w-full bg-[#050816] text-white hover:bg-[#050816]/90 rounded-lg h-11">
                  <Link to="/contact">Commission Artwork</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Website Design Section */}
      <section id="web" className="py-20 bg-[#FAF9F6] scroll-mt-24">
        <div className="container mx-auto px-6 max-w-[1200px]">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl md:text-4xl text-gray-900 mb-4">Website Design</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Custom website design and development for brands and businesses.
            </p>
          </div>

          <div className="bg-white rounded-xl p-8 border border-gray-200 shadow-sm max-w-2xl mx-auto">
            <div className="text-center mb-6">
              <p className="text-sm text-muted-foreground">Projects starting at</p>
              <p className="text-4xl font-serif font-medium text-gray-900">$2,500</p>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 mb-8">
              {[
                "Custom design",
                "Mobile responsive",
                "SEO optimization",
                "CMS integration",
                "Analytics setup",
                "Hosting consultation"
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#F5C056] mt-2"></div>
                  <span className="text-gray-600 text-sm">{item}</span>
                </div>
              ))}
            </div>

            <Button asChild className="w-full bg-[#050816] text-white hover:bg-[#050816]/90 rounded-lg h-11">
              <Link to="/contact">Request Proposal</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Back to Categories Footer */}
      <section className="py-12 bg-white border-t border-gray-200">
        <div className="container mx-auto px-6 text-center">
          <Link 
            to="/pricing" 
            className="inline-flex items-center gap-2 text-[#050816] hover:text-[#F5C056] font-medium transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to All Categories
          </Link>
        </div>
      </section>
    </div>
  );
};

export default PricingCreativeDigital;
